'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, Goal, H, Hl, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/linear-algebra'

const MatrixTransform = dynamic(() => import('../_components/widgets/LinearWidgets').then(m => m.MatrixTransform), { ssr: false })

export default function Matrices() {
  return (
    <Lesson>
      <Step>
        <H>A grid of numbers</H>
        <P>
          A <strong>matrix</strong> is a rectangle of numbers — rows and columns, like a spreadsheet. Its <strong>shape</strong>{' '}
          is written rows × columns: this one is 2 × 3.
        </P>
        <MB>{'A = \\begin{bmatrix} 1 & 2 & 0 \\\\ 3 & -1 & 2 \\end{bmatrix}'}</MB>
        <P>
          You’ve already met one: a dataset. One row per example, one column per feature. In code it’s just an array of arrays.
        </P>
        <Exercise id="matrix-shape" title="A dataset is a matrix" {...EX.matrixShape} />
      </Step>

      <Step gate="q-matvec">
        <H>Matrix × vector</H>
        <P>
          To multiply a matrix by a vector, take the dot product of <strong>each row</strong> with the vector. Each row gives one
          number of the answer.
        </P>
        <MB>{'\\begin{bmatrix} \\green{1} & \\green{2} \\\\ \\pink{3} & \\pink{4} \\end{bmatrix} \\begin{bmatrix} \\blue{5} \\\\ \\blue{6} \\end{bmatrix} = \\begin{bmatrix} \\green{1}\\cdot\\blue{5} + \\green{2}\\cdot\\blue{6} \\\\ \\pink{3}\\cdot\\blue{5} + \\pink{4}\\cdot\\blue{6} \\end{bmatrix} = \\begin{bmatrix} 17 \\\\ 39 \\end{bmatrix}'}</MB>
        <Quiz
          id="q-matvec"
          question={<M>{'\\begin{bmatrix} 2 & 0 \\\\ 0 & 3 \\end{bmatrix} \\begin{bmatrix} 4 \\\\ 1 \\end{bmatrix} = \\;?'}</M>}
          options={[{ label: <M>{'[8, 3]'}</M>, correct: true }, { label: <M>{'[8, 0, 0, 3]'}</M>, why: 'Each row produces one number, so a 2-row matrix gives a 2-number answer.' }, { label: <M>{'[2, 12]'}</M>, why: 'Row 1 is [2, 0]: 2·4 + 0·1 = 8.' }]}
          explain="Row 1: 2·4 + 0·1 = 8. Row 2: 0·4 + 3·1 = 3. This matrix stretches x by 2 and y by 3."
        />
      </Step>

      <Step>
        <H>Matrices move space</H>
        <P>
          Here’s the beautiful part. Multiply <em>every</em> point on the plane by the same matrix, and the whole grid moves
          together — stretched, rotated, sheared or flipped, but straight lines stay straight. A matrix is a{' '}
          <strong>transformation of space</strong>.
        </P>
        <Figure n="3.5" caption="Try the presets, or type your own numbers. The green and pink arrows show where the two basic arrows (î and ĵ) land — they are the matrix’s columns.">
          <MatrixTransform />
        </Figure>
        <Callout kind="plain">
          To read any 2×2 matrix at a glance: its <Hl c="green">first column</Hl> is where the arrow <M>{'[1, 0]'}</M> ends up,
          and its <Hl c="pink">second column</Hl> is where <M>{'[0, 1]'}</M> ends up. Everything else follows along.
        </Callout>
      </Step>

      <Step gate="goal-mat">
        <H>Design a transformation</H>
        <Goal
          id="goal-mat"
          task={
            <>
              Build the matrix that makes everything <strong>3 times as wide</strong> but <strong>half as tall</strong>. (Think:
              where should <M>{'[1,0]'}</M> and <M>{'[0,1]'}</M> land?)
            </>
          }
        >
          {onReach => <MatrixTransform goal={[3, 0, 0, 0.5]} onGoal={onReach} />}
        </Goal>
        <P>
          The violet square shows what happens to a 1×1 square. Its new area is called the <strong>determinant</strong>. When
          the matrix squashes everything onto a line, the area becomes 0 — information is destroyed, and no matrix can undo it.
        </P>
      </Step>

      <Step gate="mat-vec">
        <H>In code</H>
        <Exercise
          id="mat-vec"
          title="matVec"
          {...EX.matVec}
          hints={[
            <>
              <C>{'A.map(row => …)'}</C> gives one result per row.
            </>,
            <>Inside, compute the dot product of <C>row</C> with <C>v</C>.</>,
          ]}
        />
        <Callout kind="why">
          A layer of a neural network is a matrix followed by a little bend. Training adjusts the matrix so that, after a few
          layers, the data is warped into a shape where the answer is easy to read off — cats on one side, dogs on the other.
        </Callout>
      </Step>
    </Lesson>
  )
}
